class LotrSmartRingSignup {
  static SECTION_SELECTOR = '[data-lotr-smart-ring-signup-section]';
  static EMAIL_SELECTOR = '[data-lotr-smart-ring-signup-email]';
  static CHECKBOX_SELECTOR = '[data-lotr-smart-ring-signup-checkbox]';
  static SUBMIT_SELECTOR = '[data-lotr-smart-ring-signup-submit]';
  static ERROR_SELECTOR = '[data-lotr-smart-ring-signup-error]';
  static SUCCESS_SELECTOR = '[data-lotr-smart-ring-signup-success]';
  static CONTENT_SELECTOR = '[data-lotr-smart-ring-signup-content]';
  static EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  static SUCCESS_RESET_DELAY = 10000;
  static SERVER_SUCCESS_STORAGE_PREFIX = 'lotrSmartRingSignupServerSuccess:';
  static PENDING_EMAIL_STORAGE_PREFIX = 'lotrSmartRingSignupPendingEmail:';

  constructor(section) {
    this.section = section;
    this.successResetTimer = null;
    this.abortController = new AbortController();
  }

  static isValidEmail(value) {
    return LotrSmartRingSignup.EMAIL_PATTERN.test(String(value || '').trim());
  }

  static hasCustomerFormErrors(html) {
    return /<div[^>]*class="[^"]*\berrors\b[^"]*"/i.test(html || '');
  }

  static isCustomerFormSuccess(response, html) {
    if (!response.ok || LotrSmartRingSignup.hasCustomerFormErrors(html)) {
      return false;
    }

    return response.status < 400;
  }

  static getErrorMessage(section, responseText) {
    const fallbackMessage = section.querySelector(LotrSmartRingSignup.ERROR_SELECTOR)?.textContent?.trim() || 'Invalid email address';
    const errorMatch = responseText?.match(/<div class="errors"><ul><li>(.*?)<\/li><\/ul><\/div>/);

    return errorMatch?.[1] || fallbackMessage;
  }

  getStorageKey(prefix) {
    const sectionId = this.section.getAttribute('data-lotr-smart-ring-signup-section') || 'section';
    return `${prefix}${location.pathname}:${sectionId}`;
  }

  getPendingEmail() {
    try {
      return sessionStorage.getItem(this.getStorageKey(LotrSmartRingSignup.PENDING_EMAIL_STORAGE_PREFIX)) || '';
    } catch {
      return '';
    }
  }

  setPendingEmail(email) {
    try {
      sessionStorage.setItem(this.getStorageKey(LotrSmartRingSignup.PENDING_EMAIL_STORAGE_PREFIX), email);
    } catch {
      // Ignore storage failures; AJAX success still displays immediately.
    }
  }

  getServerSuccessStorageKey() {
    const email = this.section.querySelector(LotrSmartRingSignup.EMAIL_SELECTOR)?.value?.trim() || this.getPendingEmail();
    return `${this.getStorageKey(LotrSmartRingSignup.SERVER_SUCCESS_STORAGE_PREFIX)}:${email}`;
  }

  wasServerSuccessConsumed() {
    try {
      return sessionStorage.getItem(this.getServerSuccessStorageKey()) === 'true';
    } catch {
      return false;
    }
  }

  markServerSuccessConsumed() {
    try {
      sessionStorage.setItem(this.getServerSuccessStorageKey(), 'true');
    } catch {
      // Ignore storage failures; the success state still works without persistence.
    }
  }

  setEmailError(shouldShow) {
    const error = this.section.querySelector(LotrSmartRingSignup.ERROR_SELECTOR);
    const success = this.section.querySelector(LotrSmartRingSignup.SUCCESS_SELECTOR);
    const email = this.section.querySelector(LotrSmartRingSignup.EMAIL_SELECTOR);
    const field = email?.closest('.lotr-smart-ring-signup__field');

    if (error) {
      error.hidden = !shouldShow;
    }

    if (success && shouldShow) {
      success.hidden = true;
    }

    field?.classList.toggle('is-error', shouldShow);
    email?.setAttribute('aria-invalid', shouldShow ? 'true' : 'false');
  }

  clearSuccessResetTimer() {
    if (!this.successResetTimer) return;

    window.clearTimeout(this.successResetTimer);
    this.successResetTimer = null;
  }

  setSuccessState(shouldShow) {
    const content = this.section.querySelector(LotrSmartRingSignup.CONTENT_SELECTOR);
    const success = this.section.querySelector(LotrSmartRingSignup.SUCCESS_SELECTOR);

    if (success) {
      success.hidden = !shouldShow;
    }

    content?.classList.toggle('is-success', shouldShow);
  }

  resetToInitialState() {
    const email = this.section.querySelector(LotrSmartRingSignup.EMAIL_SELECTOR);
    const checkbox = this.section.querySelector(LotrSmartRingSignup.CHECKBOX_SELECTOR);

    this.clearSuccessResetTimer();
    this.setSuccessState(false);
    this.setEmailError(false);

    if (email) {
      email.value = '';
    }

    if (checkbox) {
      checkbox.checked = false;
    }

    this.section.querySelector(LotrSmartRingSignup.SUBMIT_SELECTOR)?.removeAttribute('aria-busy');
    this.updateSubmitState();
  }

  showSuccess() {
    this.clearSuccessResetTimer();
    this.setEmailError(false);
    this.setSuccessState(true);

    this.successResetTimer = window.setTimeout(
      () => this.resetToInitialState(),
      LotrSmartRingSignup.SUCCESS_RESET_DELAY,
    );
  }

  hideSuccess() {
    this.clearSuccessResetTimer();
    this.setSuccessState(false);
  }

  handleInitialServerSuccess() {
    const success = this.section.querySelector(LotrSmartRingSignup.SUCCESS_SELECTOR);
    if (success?.dataset.lotrSmartRingSignupServerSuccess !== 'true') return false;

    success.dataset.lotrSmartRingSignupServerSuccess = 'false';

    if (this.wasServerSuccessConsumed()) {
      this.resetToInitialState();
      return true;
    }

    this.markServerSuccessConsumed();
    this.showSuccess();
    return true;
  }

  showError(message) {
    const error = this.section.querySelector(LotrSmartRingSignup.ERROR_SELECTOR);

    if (error && message) {
      error.textContent = message;
    }

    this.setEmailError(true);
  }

  updateSubmitState(options = {}) {
    const email = this.section.querySelector(LotrSmartRingSignup.EMAIL_SELECTOR);
    const checkbox = this.section.querySelector(LotrSmartRingSignup.CHECKBOX_SELECTOR);
    const submit = this.section.querySelector(LotrSmartRingSignup.SUBMIT_SELECTOR);
    if (!email || !checkbox || !submit) return false;

    const emailHasValue = email.value.trim() !== '';
    const emailIsValid = LotrSmartRingSignup.isValidEmail(email.value);
    const canSubmit = emailIsValid && checkbox.checked;

    submit.disabled = !canSubmit;

    if (options.showEmailError) {
      this.setEmailError(emailHasValue && !emailIsValid);
    } else if (!emailHasValue || emailIsValid) {
      this.setEmailError(false);
    }

    return canSubmit;
  }

  refreshState() {
    this.section.querySelector(LotrSmartRingSignup.SUBMIT_SELECTOR)?.removeAttribute('aria-busy');

    if (this.handleInitialServerSuccess()) {
      return;
    }

    if (!this.section.querySelector(LotrSmartRingSignup.SUCCESS_SELECTOR)?.hidden) {
      this.showSuccess();
      return;
    }

    this.updateSubmitState();
  }

  async handleSubmit(event) {
    event?.preventDefault();

    const email = this.section.querySelector(LotrSmartRingSignup.EMAIL_SELECTOR);
    const submit = this.section.querySelector(LotrSmartRingSignup.SUBMIT_SELECTOR);
    const form = email?.closest('form');

    if (!form) return;

    if (!this.updateSubmitState({ showEmailError: true })) {
      email?.focus();
      return;
    }

    this.setPendingEmail(email?.value.trim() || '');

    submit?.setAttribute('aria-busy', 'true');
    if (submit) {
      submit.disabled = true;
    }
    this.setEmailError(false);
    this.hideSuccess();

    try {
      const response = await fetch(form.action, {
        method: form.method,
        body: new URLSearchParams(new FormData(form)),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const html = await response.text();

      if (LotrSmartRingSignup.isCustomerFormSuccess(response, html)) {
        this.showSuccess();
        return;
      }

      this.showError(LotrSmartRingSignup.getErrorMessage(this.section, html));
    } catch (error) {
      console.error('Error:', error);
      this.showError();
    } finally {
      submit?.removeAttribute('aria-busy');
      this.updateSubmitState();
    }
  }

  bindEvents() {
    const { signal } = this.abortController;
    const email = this.section.querySelector(LotrSmartRingSignup.EMAIL_SELECTOR);
    const checkbox = this.section.querySelector(LotrSmartRingSignup.CHECKBOX_SELECTOR);
    const submit = this.section.querySelector(LotrSmartRingSignup.SUBMIT_SELECTOR);

    email?.addEventListener(
      'input',
      () => {
        this.hideSuccess();
        this.updateSubmitState();
      },
      { signal },
    );

    email?.addEventListener('blur', () => this.updateSubmitState({ showEmailError: true }), { signal });
    checkbox?.addEventListener('change', () => this.updateSubmitState(), { signal });
    submit?.addEventListener('click', (event) => this.handleSubmit(event), { signal });
  }

  init() {
    if (!this.section) return;

    if (this.section.dataset.smartRingSignupInitialized === 'true') {
      this.refreshState();
      return;
    }

    this.bindEvents();
    this.refreshState();

    this.section.dataset.smartRingSignupInitialized = 'true';
    this.section._lotrSmartRingSignupInstance = this;
  }

  destroy() {
    this.clearSuccessResetTimer();
    this.abortController.abort();

    delete this.section._lotrSmartRingSignupInstance;
    delete this.section.dataset.smartRingSignupInitialized;
  }

  static getSections(root = document) {
    const sections = [];

    if (root.matches?.(LotrSmartRingSignup.SECTION_SELECTOR)) {
      sections.push(root);
    }

    root.querySelectorAll?.(LotrSmartRingSignup.SECTION_SELECTOR).forEach((section) => sections.push(section));
    return sections;
  }

  static initializeAll(root = document) {
    LotrSmartRingSignup.getSections(root).forEach((section) => {
      new LotrSmartRingSignup(section).init();
    });
  }

  static refreshAll(root = document) {
    LotrSmartRingSignup.getSections(root).forEach((section) => {
      section._lotrSmartRingSignupInstance?.refreshState();
    });
  }

  static destroyAllIn(root) {
    LotrSmartRingSignup.getSections(root).forEach((section) => {
      section._lotrSmartRingSignupInstance?.destroy();
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => LotrSmartRingSignup.initializeAll());
} else {
  LotrSmartRingSignup.initializeAll();
}

window.addEventListener('pageshow', () => {
  LotrSmartRingSignup.refreshAll();
});

document.addEventListener('shopify:section:load', (event) => {
  LotrSmartRingSignup.initializeAll(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  LotrSmartRingSignup.destroyAllIn(event.target);
});
